/**
 * Coalesce concurrent identical requests (e.g. React 18 StrictMode dev double-mount).
 * While the first request is in flight, callers with the same key share one promise.
 *
 * Use one `Map` per logical API surface (usually per service singleton). Keys must
 * distinguish different requests (e.g. `"list"` vs `summary:${start}` vs serialized list params).
 *
 * @example
 * private readonly inflight = new Map<string, Promise<unknown>>();
 * getList = () =>
 *   dedupeConcurrentRequest(this.inflight, "list", async () => {
 *     const res = await fetch("/api/items/");
 *     return res.json();
 *   });
 */
export function dedupeConcurrentRequest<K extends string, T>(
  inflight: Map<K, Promise<T>>,
  key: K,
  run: () => Promise<T>,
): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const promise = run().finally(() => {
    inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
}
